/* Spaced retrieval practice: the "forge" behind the RPG layer.
 *
 * Every completed level unlocks review cards:
 *   - question cards: the level's `review` questions, or the questions of a quiz level
 *   - a reforge card for coding levels: solve the exercise again from the starter code
 *
 * Cards follow a Leitner schedule. A correct answer moves a card up one box and pushes its next
 * review further away; a miss sends it back to box 0. Retrieving something just before you would
 * forget it is what makes it stick, so the schedule - not grinding - is what raises mastery.
 *
 * Everything here is pure: functions take the progress state and `now` (ms) and return data.
 */

const DAY = 24 * 60 * 60 * 1000;

export const MAX_BOX = 5;
// Days until the next review after landing in box N.
export const QUESTION_INTERVALS = [1, 3, 7, 16, 35, 80];
export const REFORGE_INTERVALS = [3, 10, 30, 60, 120, 240];
export const SESSION_SIZE = 15;

export const RANKS = [
  { id: "apprentice", points: 0 },
  { id: "journeyman", points: 25 },
  { id: "artisan", points: 80 },
  { id: "smith", points: 180 },
  { id: "master", points: 350 },
  { id: "grandmaster", points: 600 },
];

function intervalsFor(card) {
  return card.kind === "reforge" ? REFORGE_INTERVALS : QUESTION_INTERVALS;
}

/** All cards a track can produce, in study order. */
export function buildDeck(track) {
  const deck = [];
  for (const chapter of track.chapters) {
    for (const level of chapter.levels) {
      const base = { levelId: level.id, levelTitle: level.title, chapterId: chapter.id, concepts: level.concepts || [] };
      const questions = level.type === "quiz" ? level.questions : level.review || [];
      questions.forEach((question, index) => {
        deck.push({ ...base, id: `${level.id}#q${index}`, kind: "question", question });
      });
      if (level.type === "code") {
        deck.push({ ...base, id: `${level.id}#reforge`, kind: "reforge", level });
      }
    }
  }
  return deck;
}

/**
 * Scheduling state of one card, or null while its level is not completed.
 * New cards start in box 0 and become due one interval after the level was completed.
 */
export function cardState(card, state) {
  const solvedAt = state.solved[card.levelId];
  if (!solvedAt) return null;
  const stored = state.cards?.[card.id];
  if (stored) return stored;
  return { box: 0, due: Date.parse(solvedAt) + intervalsFor(card)[0] * DAY, reviews: 0, lapses: 0 };
}

/** New scheduling state after a review. */
export function scheduleReview(card, previous, correct, now) {
  const intervals = intervalsFor(card);
  const box = correct ? Math.min(previous.box + 1, MAX_BOX) : 0;
  return {
    box,
    due: now + intervals[box] * DAY,
    reviews: previous.reviews + 1,
    lapses: previous.lapses + (correct ? 0 : 1),
    last: now,
    recovered: (previous.recovered || 0) + (correct && previous.lapses > 0 && previous.box === 0 ? 1 : 0),
  };
}

export function unlockedCards(deck, state) {
  return deck.map((card) => ({ card, schedule: cardState(card, state) })).filter((entry) => entry.schedule);
}

export function dueCards(deck, state, now) {
  return unlockedCards(deck, state)
    .filter((entry) => entry.schedule.due <= now)
    .sort((a, b) => a.schedule.due - b.schedule.due);
}

export function nextDueAt(deck, state) {
  const dues = unlockedCards(deck, state).map((entry) => entry.schedule.due);
  return dues.length ? Math.min(...dues) : null;
}

/** Deterministic PRNG so a session keeps its order across re-renders. */
function mulberry32(seed) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Pick the most overdue cards, then interleave them: mixing concepts forces you to recognise which
 * idea a problem needs, instead of repeating the answer you just gave.
 */
export function buildSession(deck, state, now, { size = SESSION_SIZE, seed = now } = {}) {
  const picked = dueCards(deck, state, now).slice(0, size).map((entry) => entry.card);
  const random = mulberry32(Math.floor(seed));
  for (let i = picked.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [picked[i], picked[j]] = [picked[j], picked[i]];
  }
  // Avoid two cards from the same level back to back when another order is possible.
  for (let i = 1; i < picked.length; i += 1) {
    if (picked[i].levelId === picked[i - 1].levelId) {
      const swap = picked.findIndex((card, k) => k > i && card.levelId !== picked[i - 1].levelId);
      if (swap !== -1) [picked[i], picked[swap]] = [picked[swap], picked[i]];
    }
  }
  return picked;
}

/** Mastery of each concept: the average box of its unlocked cards, from 0 to 5. */
export function conceptMastery(deck, state, now) {
  const byConcept = new Map();
  for (const { card, schedule } of unlockedCards(deck, state)) {
    for (const concept of card.concepts) {
      const entry = byConcept.get(concept) || { boxes: 0, cards: 0, due: 0 };
      entry.boxes += schedule.box;
      entry.cards += 1;
      if (schedule.due <= now) entry.due += 1;
      byConcept.set(concept, entry);
    }
  }
  const result = {};
  byConcept.forEach((entry, concept) => {
    result[concept] = { level: Math.floor(entry.boxes / entry.cards), cards: entry.cards, due: entry.due };
  });
  return result;
}

/** Retained knowledge: one point per box climbed by every card, plus one per completed level. */
export function masteryPoints(deck, state) {
  const boxes = unlockedCards(deck, state).reduce((sum, entry) => sum + entry.schedule.box, 0);
  return boxes + Object.keys(state.solved).length;
}

export function rankFor(points) {
  let index = 0;
  RANKS.forEach((rank, i) => {
    if (points >= rank.points) index = i;
  });
  const next = RANKS[index + 1] || null;
  return { ...RANKS[index], index, next };
}

/* ------------------------------------------------------------------ achievements */

export const ACHIEVEMENTS = [
  "first-level",
  "self-reliant",
  "honest-smith",
  "sharp-eye",
  "back-to-the-anvil",
  "comeback",
  "habit",
  "iron-discipline",
  "reforged",
  "masterwork",
  "boss-slayer",
  "legend",
];

/** Which achievements the current state has earned. Recorded ones are never taken away. */
export function earnedAchievements(track, deck, state, now) {
  const levels = track.chapters.flatMap((chapter) => chapter.levels);
  const stats = state.levelStats || {};
  const solvedCode = levels.filter((level) => level.type === "code" && state.solved[level.id]);
  const bosses = levels.filter((level) => level.boss);
  const cards = Object.values(state.cards || {});
  const days = state.reviewDays || [];
  const mastery = conceptMastery(deck, state, now);

  const checks = {
    "first-level": Object.keys(state.solved).length >= 1,
    "self-reliant": solvedCode.filter((level) => stats[level.id]?.solvedWithoutHints).length >= 5,
    "honest-smith": solvedCode.filter((level) => stats[level.id]?.solvedWithoutReveal).length >= 10,
    "sharp-eye": levels.some((level) => stats[level.id]?.perfectQuiz),
    "back-to-the-anvil": cards.some((card) => card.reviews > 0),
    comeback: cards.some((card) => card.recovered > 0),
    habit: days.length >= 3,
    "iron-discipline": days.length >= 7,
    reforged: Object.entries(state.cards || {}).some(([id, card]) => id.endsWith("#reforge") && card.box > 0),
    masterwork: Object.values(mastery).some((entry) => entry.level >= MAX_BOX),
    "boss-slayer": bosses.some((level) => state.solved[level.id]),
    legend: bosses.length > 0 && bosses.every((level) => state.solved[level.id]),
  };
  return ACHIEVEMENTS.filter((id) => checks[id]);
}

export function toDayKey(now) {
  const date = new Date(now);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export const DAY_MS = DAY;
